/**
 * useShipmentWebSocket Hook
 * 
 * Provides real-time shipment updates via WebSocket.
 * Listens for shipment events and triggers callbacks for updates.
 * 
 * Usage:
 * const { onUpdate } = useShipmentWebSocket({
 *   onShipmentCreated: (shipment) => { ... },
 *   onShipmentUpdated: (shipment, changes) => { ... },
 *   onStatusChanged: (shipment, oldStatus, newStatus) => { ... }
 * });
 */
import { useEffect, useRef, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { toast } from 'sonner';
import { playNotificationSound, vibrate } from '../services/pushNotifications';

// Event types matching backend
export const ShipmentEventType = {
  CREATED: 'shipment_created',
  UPDATED: 'shipment_updated',
  DELETED: 'shipment_deleted',
  STATUS_CHANGED: 'shipment_status_changed',
  ASSIGNED: 'shipment_assigned',
  UNASSIGNED: 'shipment_unassigned',
  AVAILABILITY_UPDATED: 'shipment_availability_updated',
  HOLD_CHANGED: 'shipment_hold_changed',
  LOCATION_UPDATED: 'shipment_location_updated',
  BULK_UPDATED: 'shipments_bulk_updated',
  DOCUMENT_ADDED: 'shipment_document_added'
};

/**
 * Hook for real-time shipment WebSocket events
 */
export const useShipmentWebSocket = ({
  onShipmentCreated,
  onShipmentUpdated,
  onShipmentDeleted,
  onStatusChanged,
  onAvailabilityUpdated,
  onDriverAssigned,
  onBulkUpdated,
  onAnyEvent,
  // Settings
  showToasts = true,
  playSound = true,
  autoRefresh = true
} = {}) => {
  
  // Store callbacks in refs to avoid stale closures
  const callbacksRef = useRef({
    onShipmentCreated,
    onShipmentUpdated,
    onShipmentDeleted,
    onStatusChanged,
    onAvailabilityUpdated,
    onDriverAssigned,
    onBulkUpdated,
    onAnyEvent
  });
  
  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onShipmentCreated,
      onShipmentUpdated,
      onShipmentDeleted,
      onStatusChanged,
      onAvailabilityUpdated,
      onDriverAssigned,
      onBulkUpdated,
      onAnyEvent
    };
  }, [onShipmentCreated, onShipmentUpdated, onShipmentDeleted, onStatusChanged, 
      onAvailabilityUpdated, onDriverAssigned, onBulkUpdated, onAnyEvent]);
  
  // Handle incoming WebSocket messages
  const handleMessage = useCallback((data) => {
    const eventType = data.type;
    const shipment = data.shipment;
    const changes = data.changes;
    const priority = data.priority || 'normal';
    
    // Check if this is a shipment event
    if (!Object.values(ShipmentEventType).includes(eventType)) {
      return; // Not a shipment event
    }
    
    // Play sound for high priority events
    if (playSound && (priority === 'high' || priority === 'critical')) {
      try {
        playNotificationSound(priority);
        vibrate([100, 50, 100]);
      } catch {
        // notification sound/vibration unsupported on this device
      }
    }
    
    // Call onAnyEvent for all shipment events
    if (callbacksRef.current.onAnyEvent) {
      callbacksRef.current.onAnyEvent(eventType, data);
    }
    
    // Handle specific event types
    switch (eventType) {
      case ShipmentEventType.CREATED:
        if (showToasts) {
          toast.success('New Shipment Created', {
            description: `Container: ${shipment?.container_number || 'N/A'}`,
            duration: 5000
          });
        }
        if (callbacksRef.current.onShipmentCreated) {
          callbacksRef.current.onShipmentCreated(shipment);
        }
        break;
        
      case ShipmentEventType.UPDATED:
        if (showToasts && changes && Object.keys(changes).length > 0) {
          const changedFields = Object.keys(changes).join(', ');
          toast.info('Shipment Updated', {
            description: `${shipment?.container_number}: ${changedFields}`,
            duration: 4000
          });
        }
        if (callbacksRef.current.onShipmentUpdated) {
          callbacksRef.current.onShipmentUpdated(shipment, changes);
        }
        break;
        
      case ShipmentEventType.DELETED:
        if (showToasts) {
          toast.warning('Shipment Deleted', {
            description: `Container: ${shipment?.container_number || 'N/A'}`,
            duration: 5000
          });
        }
        if (callbacksRef.current.onShipmentDeleted) {
          callbacksRef.current.onShipmentDeleted(shipment?.id, shipment?.container_number);
        }
        break;
        
      case ShipmentEventType.STATUS_CHANGED:
        if (showToasts) {
          const oldStatus = changes?.status?.old || 'Unknown';
          const newStatus = changes?.status?.new || 'Unknown';
          toast.info('Status Changed', {
            description: `${shipment?.container_number}: ${oldStatus} → ${newStatus}`,
            duration: 5000
          });
        }
        if (callbacksRef.current.onStatusChanged) {
          callbacksRef.current.onStatusChanged(
            shipment, 
            changes?.status?.old, 
            changes?.status?.new
          );
        }
        break;
        
      case ShipmentEventType.AVAILABILITY_UPDATED:
        if (showToasts) {
          const lfd = changes?.availability?.lfd;
          const hasHolds = changes?.availability?.has_holds || changes?.availability?.holds?.length > 0;
          toast.info('Availability Updated', {
            description: `${shipment?.container_number}${lfd ? ` - LFD: ${lfd}` : ''}${hasHolds ? ' (HOLDS)' : ''}`,
            duration: 5000,
            className: hasHolds ? 'border-amber-500' : ''
          });
        }
        if (callbacksRef.current.onAvailabilityUpdated) {
          callbacksRef.current.onAvailabilityUpdated(shipment, changes?.availability);
        }
        break;
        
      case ShipmentEventType.ASSIGNED:
        if (showToasts) {
          const driverName = changes?.driver?.name || 'Unknown Driver';
          toast.success('Driver Assigned', {
            description: `${shipment?.container_number} → ${driverName}`,
            duration: 5000
          });
        }
        if (callbacksRef.current.onDriverAssigned) {
          callbacksRef.current.onDriverAssigned(
            shipment, 
            changes?.driver?.id, 
            changes?.driver?.name
          );
        }
        break;
        
      case ShipmentEventType.BULK_UPDATED:
        if (showToasts) {
          toast.info('Bulk Update', {
            description: `${shipment?.count || 0} shipments ${shipment?.update_type || 'updated'}`,
            duration: 5000
          });
        }
        if (callbacksRef.current.onBulkUpdated) {
          callbacksRef.current.onBulkUpdated(
            shipment?.count, 
            shipment?.update_type, 
            shipment?.details
          );
        }
        break;
        
      default:
        // Unknown event type
        console.log('Unknown shipment event:', eventType);
    }
  }, [showToasts, playSound]);
  
  // Use the main WebSocket hook
  const { connected, sendMessage, reconnect } = useWebSocket(handleMessage);
  
  return {
    connected,
    reconnect,
    // Expose event types for consumers
    ShipmentEventType
  };
};

/**
 * Simple wrapper for components that just need to refresh on any shipment change
 */
export const useShipmentRefresh = (refreshCallback) => {
  const { connected } = useShipmentWebSocket({
    onShipmentCreated: refreshCallback,
    onShipmentUpdated: refreshCallback,
    onShipmentDeleted: refreshCallback,
    onStatusChanged: refreshCallback,
    onBulkUpdated: refreshCallback,
    showToasts: false,
    playSound: false
  });
  
  return { connected };
};

export default useShipmentWebSocket;
